import { useMemo } from 'react';
import { estimateEnergyBalance } from '../logic/energy';
import { weightTrend } from '../body';
import { StatTile } from '../ui';
import { EnergyBalanceCard } from './EnergyBalanceCard';
import { NutritionChartCard } from './NutritionChartCard';
import { NutritionWeeksCard } from './NutritionWeeksCard';
import { TopFoodsCard } from './TopFoodsCard';
import { fmtInt, nutritionOverview, proteinStreak, signed, weekTable } from './nutritionData';

/* Stats > Nutrition: how the food log adds up over weeks. Today is left out
 * of every average (it is never complete); the streak counts it once hit. */
export function NutritionView({ t, isDark, state, setState, today, onOpenDay }) {
  const { nutrition, logStatus, profile, bodyMetrics, supplements } = state;
  // Only the fields the maths reads, so a ticked set doesn't recompute it.
  const nState = useMemo(
    () => ({ nutrition, logStatus, profile, bodyMetrics, supplements }),
    [nutrition, logStatus, profile, bodyMetrics, supplements]
  );
  const overview = useMemo(() => nutritionOverview(nState, today), [nState, today]);
  const weeks = useMemo(() => weekTable(nState, today), [nState, today]);
  const est = useMemo(() => estimateEnergyBalance(nState, today), [nState, today]);
  const streak = useMemo(
    () => proteinStreak(nutrition, today, overview ? overview.targets.protein : 0),
    [nutrition, today, overview]
  );
  const weight = useMemo(() => {
    const tr = weightTrend(bodyMetrics, today);
    return tr ? (tr.avg != null ? tr.avg : tr.latest) : null;
  }, [bodyMetrics, today]);

  if (!overview) return null;
  const { targets, recent, loggedDays, days } = overview;
  const kcalDiff = recent && targets.kcal > 0 ? recent.kcal - targets.kcal : null;
  const onKcal = kcalDiff !== null && Math.abs(kcalDiff) * 10 <= targets.kcal;
  const proteinOk = recent && targets.protein > 0 && recent.protein * 10 >= targets.protein * 9;
  const perKg = recent && weight ? (recent.protein / weight).toFixed(1) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="fit-quad" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <StatTile t={t} label="Avg kcal" value={recent ? fmtInt(recent.kcal) : '—'} accent={onKcal}
          hint={!recent ? 'no complete days yet'
            : `${kcalDiff !== null ? `${signed(kcalDiff, 0)} vs target · ` : ''}last ${recent.days} d`} />
        <StatTile t={t} label="Avg protein" value={recent ? `${fmtInt(recent.protein)} g` : '—'} accent={!!proteinOk}
          hint={!recent ? `target ${fmtInt(targets.protein)} g`
            : `${perKg ? `${perKg} g/kg · ` : ''}target ${fmtInt(targets.protein)}`} />
        <StatTile t={t} label="Logged days" value={`${loggedDays}/${days}`} accent={loggedDays * 4 >= days * 3}
          hint="complete days" />
        <StatTile t={t} label="Protein streak" value={`${streak.current}d`} accent={streak.current >= 3}
          hint={targets.protein > 0 ? `≥ ${fmtInt(targets.protein * 0.9)} g${streak.todayHit ? ' · incl. today' : ''}` : 'no protein target'} />
      </div>

      <NutritionChartCard t={t} rows={overview.rows} targets={targets} onOpenDay={onOpenDay} />

      <EnergyBalanceCard t={t} isDark={isDark} est={est} profile={profile} setState={setState} onOpenDay={onOpenDay} />

      <NutritionWeeksCard t={t} weeks={weeks} />

      <TopFoodsCard t={t} nutrition={nutrition} from={overview.from} to={overview.to} days={days} />
    </div>
  );
}
